/**
 * The printable question pack — every question a learner may see, on paper.
 *
 * Numbering runs continuously across all three sections so the answer key at the
 * back is never ambiguous. The key is a separate section rather than answers
 * printed under each question, because a student self-testing should not have
 * the answer in peripheral vision.
 *
 * Nothing here decides what a learner may see. The API already withheld the
 * bank answers this learner has not unlocked, so `answer === null` is a fact,
 * not a display choice.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Printer } from '@phosphor-icons/react';
import { api, type QuestionPack as Pack } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Button, EmptyState, ErrorState, LoadingState, cx } from '../components/ui';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

const TOPIC_LABELS: Record<string, string> = {
  'entra-iam': 'Entra ID & IAM',
  sentinel: 'Microsoft Sentinel',
  'incident-response': 'Incident response',
  general: 'General',
};
const topicLabel = (t: string) => TOPIC_LABELS[t] ?? t;

/** Ruled writing space, sized from how much the question expects. */
function Lines({ n, hidden }: { n: number; hidden: boolean }) {
  if (hidden) return null;
  return (
    <div
      className="mt-2.5"
      aria-hidden="true"
      style={{
        height: `${n * 26}px`,
        backgroundImage: 'linear-gradient(to bottom, transparent 25px, #cbd5e1 25px)',
        backgroundSize: '100% 26px',
      }}
    />
  );
}

function Tag({ children, tone = 'plain' }: { children: React.ReactNode; tone?: 'plain' | 'teal' | 'violet' }) {
  return (
    <span
      className={cx(
        'whitespace-nowrap rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.1em]',
        tone === 'teal' && 'border-teal-200 bg-teal-50 text-teal-700',
        tone === 'violet' && 'border-violet-200 bg-violet-50 text-violet-700',
        tone === 'plain' && 'border-slate-200 text-slate-500',
      )}
    >
      {children}
    </span>
  );
}

function Q({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <div className="mb-5 flex gap-3.5" style={{ breakInside: 'avoid' }}>
      <div className="w-7 flex-none pt-px font-display text-[15px] font-bold tabular-nums text-teal-700">{n}</div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function SectionHead({ title, meta, note }: { title: string; meta: string; note: string }) {
  return (
    <>
      <div className="mb-1.5 flex items-baseline justify-between gap-4 border-b-2 border-brand-navy pb-3">
        <h2 className="m-0 font-display text-[25px] font-bold text-brand-navy">{title}</h2>
        <span className="label-mono whitespace-nowrap">{meta}</span>
      </div>
      <p className="mb-6 mt-2.5 text-[13.5px] text-slate-500">{note}</p>
    </>
  );
}

function GroupHead({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 mt-7 border-b border-slate-200 pb-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-teal-700">
      {children}
    </div>
  );
}

type Mode = 'workbook' | 'reading';

export function QuestionPack() {
  const s = useAsync(() => api.questionPack(), []);
  const [mode, setMode] = useState<Mode>('workbook');
  const [show, setShow] = useState({ quiz: true, practice: true, bank: true });
  const reading = mode === 'reading';
  // Writing space is the point of the workbook and dead weight in reading mode.
  const compact = reading;

  // One running number across every section, so the key can be keyed by it.
  const numbered = useMemo(() => {
    if (!s.data) return null;
    let n = 0;
    const quiz = s.data.quizzes.map((g) => ({ ...g, questions: g.questions.map((q) => ({ ...q, n: ++n })) }));
    const practice = s.data.practice.map((g) => ({ ...g, questions: g.questions.map((q) => ({ ...q, n: ++n })) }));
    const bank = s.data.bank.map((g) => ({ ...g, questions: g.questions.map((q) => ({ ...q, n: ++n })) }));
    return { quiz, practice, bank, last: n };
  }, [s.data]);

  if (s.loading) return <LoadingState />;
  if (s.error || !s.data) return <ErrorState message={s.error ?? 'Failed to load'} onRetry={s.reload} />;

  const pack: Pack = s.data;
  const { tally } = pack;
  const total = tally.quiz + tally.practice + tally.bank;
  if (total === 0) {
    return (
      <EmptyState
        title="No questions yet"
        description="Once your course has quizzes, practice questions or interview questions, they will appear here ready to print."
      />
    );
  }
  const g = numbered!;
  const range = (arr: { questions: { n: number }[] }[]) => {
    const ns = arr.flatMap((x) => x.questions.map((q) => q.n));
    return ns.length ? `Q${ns[0]}–Q${ns[ns.length - 1]}` : '';
  };

  return (
    <div className="mx-auto max-w-4xl">
      {/* Screen chrome — never printed. */}
      <div className="mb-6 flex flex-wrap items-center gap-3 print:hidden">
        <Link to="/interview" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-teal-700">
          <ArrowLeft size={16} /> Back
        </Link>
        <span className="flex-1" />
        <div className="flex items-center gap-3 text-sm text-slate-600">
          {(['quiz', 'practice', 'bank'] as const).map((k) => (
            <label key={k} className="flex cursor-pointer items-center gap-1.5">
              <input
                type="checkbox"
                checked={show[k]}
                onChange={(e) => setShow({ ...show, [k]: e.target.checked })}
                className="h-4 w-4 accent-teal-600"
              />
              {k === 'quiz' ? 'Quizzes' : k === 'practice' ? 'Practice' : 'Interview'}
            </label>
          ))}
        </div>
        <div className="flex overflow-hidden rounded-full border border-slate-200">
          {(['workbook', 'reading'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={cx(
                'px-3.5 py-1.5 text-sm font-semibold',
                mode === m ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
              )}
            >
              {m === 'workbook' ? 'Workbook' : 'Reading'}
            </button>
          ))}
        </div>
        <Button onClick={() => window.print()}>
          <Printer size={16} weight="bold" /> Print / PDF
        </Button>
      </div>
      <p className="mb-6 text-[13px] text-slate-500 print:hidden">
        {reading
          ? 'Reading mode — the answer sits under each question, with its key points as bullets, and there is no writing space. Built for studying away from a screen.'
          : 'Workbook mode — space to write, and the answers in a key at the back so you can test yourself first.'}
      </p>

      {/* ── header band ─────────────────────────────────────────────── */}
      <div
        className="mb-8 rounded-2xl px-7 py-6 text-white"
        style={{ background: 'linear-gradient(160deg,#0b1530 0%,#142244 100%)', printColorAdjust: 'exact' }}
      >
        <div className="mb-2.5 font-mono text-[11px] uppercase tracking-[0.16em] text-teal-300">
          CyberVault School · Revision pack
        </div>
        <h1 className="m-0 font-display text-[27px] font-extrabold leading-tight text-white">
          All questions, in one place
        </h1>
        <p className="m-0 mt-1.5 text-sm text-slate-300">
          <strong className="text-white">{pack.printedFor}</strong>
          {pack.courses.length > 0 && <> · {pack.courses.join(', ')}</>} · printed{' '}
          {new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
        <div className="mt-4 flex flex-wrap gap-7 border-t border-teal-400/25 pt-4 text-[13px] text-slate-300">
          <span>
            <b className="block font-display text-xl font-bold text-white">{tally.quiz}</b>Quiz questions
          </span>
          <span>
            <b className="block font-display text-xl font-bold text-white">{tally.practice}</b>Practice questions
          </span>
          <span>
            <b className="block font-display text-xl font-bold text-white">{tally.bank}</b>Interview questions
          </span>
          <span>
            <b className="block font-display text-xl font-bold text-white">{tally.unlocked}</b>Answers unlocked
          </span>
        </div>
      </div>

      {/* ── 1 · quizzes ─────────────────────────────────────────────── */}
      {show.quiz && g.quiz.length > 0 && (
        <section>
          <SectionHead
            title="1 · Quizzes"
            meta={`${range(g.quiz)} · multiple choice`}
            note={
              reading
                ? 'The correct option is marked, with the explanation underneath.'
                : 'Circle one answer per question. Correct answers and explanations are in the key at the back.'
            }
          />
          {g.quiz.map((grp) => (
            <div key={grp.quizId}>
              <GroupHead>
                {grp.moduleTitle} · {grp.title}
              </GroupHead>
              {grp.questions.map((q) => (
                <Q key={q.id} n={q.n}>
                  <p className="m-0 text-slate-800">{q.prompt}</p>
                  <div className="mt-2.5 flex flex-col gap-1.5">
                    {q.options.map((opt, i) => {
                      const isRight = reading && i === q.correctIndex;
                      return (
                        <div key={i} className="flex items-start gap-2.5 text-[14.5px]">
                          <span
                            className={cx(
                              'mt-0.5 h-[15px] w-[15px] flex-none rounded-full border text-center font-mono text-[9px] leading-[14px]',
                              isRight
                                ? 'border-teal-600 bg-teal-600 font-bold text-white'
                                : 'border-slate-300 text-slate-500',
                            )}
                          >
                            {LETTERS[i]}
                          </span>
                          <span className={cx(isRight && 'font-semibold text-teal-800')}>{opt}</span>
                        </div>
                      );
                    })}
                  </div>
                  {reading && q.explanation && (
                    <p className="mt-2 border-l-[3px] border-teal-500 bg-teal-50/60 px-3.5 py-2 text-[13.5px] leading-relaxed text-slate-700">
                      {q.explanation}
                    </p>
                  )}
                </Q>
              ))}
            </div>
          ))}
        </section>
      )}

      {/* ── 2 · practice ────────────────────────────────────────────── */}
      {show.practice && g.practice.length > 0 && (
        <section style={{ breakBefore: 'page' }} className="mt-10">
          <SectionHead
            title="2 · Practice questions"
            meta={`${range(g.practice)} · write your answer`}
            note={
              reading
                ? 'Open-ended. The key points come first, then the full model answer.'
                : 'Open-ended. Write your answer, then compare it against the model answer in the key.'
            }
          />
          {g.practice.map((grp) => (
            <div key={grp.moduleId}>
              <GroupHead>
                {grp.sectionTitle ? `${grp.sectionTitle} · ` : ''}
                {grp.moduleTitle}
              </GroupHead>
              {grp.questions.map((q) => (
                <Q key={q.id} n={q.n}>
                  <p className="m-0 text-slate-800">{q.prompt}</p>
                  {!reading && q.keyPointCount > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <Tag tone="teal">{q.keyPointCount} key points expected</Tag>
                    </div>
                  )}
                  {reading && <Inline answer={q.modelAnswer} keyPoints={q.keyPoints} />}
                  <Lines n={Math.min(6, Math.max(3, q.keyPointCount))} hidden={compact} />
                </Q>
              ))}
            </div>
          ))}
        </section>
      )}

      {/* ── 3 · interview bank ──────────────────────────────────────── */}
      {show.bank && g.bank.length > 0 && (
        <section style={{ breakBefore: 'page' }} className="mt-10">
          <SectionHead
            title="3 · Interview questions"
            meta={`${range(g.bank)} · spoken practice`}
            note={
              reading
                ? 'Answers appear under each question, for the ones you have unlocked by attempting them in the app.'
                : 'Answer these out loud before you look anything up. Model answers appear in the key only for questions you have already attempted in the app — the same rule Interview Prep uses.'
            }
          />
          {g.bank.map((grp) => (
            <div key={grp.topic}>
              <GroupHead>
                {topicLabel(grp.topic)} · {grp.questions.length} questions
              </GroupHead>
              {grp.questions.map((q) => (
                <Q key={q.id} n={q.n}>
                  <p className="m-0 text-slate-800">{q.question}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Tag tone={q.kind === 'behavioural' ? 'violet' : 'plain'}>{q.kind}</Tag>
                    {q.subTopic && <Tag>{q.subTopic}</Tag>}
                    {!reading && q.keyPointCount > 0 && (
                      <Tag tone="teal">{q.keyPointCount} key points expected</Tag>
                    )}
                  </div>
                  {reading &&
                    (q.answer || q.keyPoints ? (
                      <Inline answer={q.answer} keyPoints={q.keyPoints} behavioural={q.kind === 'behavioural'} />
                    ) : (
                      <p className="mt-2 text-[13px] italic text-slate-500">
                        {q.hasAnswer
                          ? 'Answer this in Interview Prep to unlock the model answer.'
                          : 'No model answer recorded — worth asking your instructor.'}
                      </p>
                    ))}
                  <Lines n={Math.min(6, Math.max(3, q.keyPointCount))} hidden={compact} />
                </Q>
              ))}
            </div>
          ))}
        </section>
      )}

      {/* ── answer key — workbook only; reading mode prints answers inline ── */}
      {!reading && (
      <section style={{ breakBefore: 'page' }} className="mt-10">
        <SectionHead
          title="Answer key"
          meta="Do not read ahead"
          note="Numbered to match. Interview answers you have not unlocked yet are marked — answer them in the app and they will be here the next time you print."
        />

        {g.quiz.length > 0 && <GroupHead>Quizzes</GroupHead>}
        {g.quiz.flatMap((grp) =>
          grp.questions.map((q) => (
            <KeyRow key={q.id} n={q.n} prompt={q.prompt}>
              <p className="m-0 mb-1.5 font-semibold text-teal-700">
                {LETTERS[q.correctIndex]} — {q.options[q.correctIndex]}
              </p>
              {q.explanation && <p className="m-0">{q.explanation}</p>}
            </KeyRow>
          )),
        )}

        {g.practice.length > 0 && <GroupHead>Practice questions</GroupHead>}
        {g.practice.flatMap((grp) =>
          grp.questions.map((q) => (
            <KeyRow key={q.id} n={q.n} prompt={q.prompt}>
              <p className="m-0 whitespace-pre-wrap">{q.modelAnswer}</p>
            </KeyRow>
          )),
        )}

        {g.bank.length > 0 && <GroupHead>Interview questions</GroupHead>}
        {g.bank.flatMap((grp) =>
          grp.questions.map((q) => (
            <KeyRow key={q.id} n={q.n} prompt={q.question}>
              {q.kind === 'behavioural' && q.answer ? (
                <div className="rounded-r-lg border-l-[3px] border-violet-500 bg-violet-50 px-3.5 py-2.5 text-[13.5px]">
                  <strong>Behavioural — coaching frame, not a model answer.</strong> There is no correct answer to your
                  own career history.
                  <p className="m-0 mt-1.5 whitespace-pre-wrap">{q.answer}</p>
                </div>
              ) : q.answer ? (
                <p className="m-0 whitespace-pre-wrap">{q.answer}</p>
              ) : !q.hasAnswer ? (
                <div className="rounded-r-lg border-l-[3px] border-amber-700 bg-amber-50 px-3.5 py-2.5 text-[13.5px] text-amber-900">
                  <strong>No model answer recorded.</strong> The {q.keyPointCount} key points in the app are what a
                  strong answer covers.
                </div>
              ) : (
                <div className="rounded-r-lg border-l-[3px] border-slate-600 bg-slate-50 px-3.5 py-2.5 text-[13.5px] text-slate-600">
                  <strong>Not unlocked yet.</strong> Answer this in Interview Prep and the model answer will be here the
                  next time you print.
                </div>
              )}
            </KeyRow>
          )),
        )}
      </section>
      )}
    </div>
  );
}


/**
 * Reading mode's answer block. Key points come first as bullets because they are
 * the part you can absorb in the ten seconds between tables; the prose answer is
 * underneath for when there is longer.
 */
function Inline({
  answer,
  keyPoints,
  behavioural = false,
}: {
  answer: string | null;
  keyPoints: string[] | null;
  behavioural?: boolean;
}) {
  return (
    <div
      className={cx(
        'mt-2 rounded-r-lg border-l-[3px] px-3.5 py-2.5',
        behavioural ? 'border-violet-400 bg-violet-50' : 'border-teal-500 bg-teal-50/60',
      )}
      style={{ breakInside: 'avoid' }}
    >
      {behavioural && (
        <p className="m-0 mb-1.5 font-mono text-[10px] uppercase tracking-[0.1em] text-violet-700">
          Coaching frame — no single right answer
        </p>
      )}
      {keyPoints && keyPoints.length > 0 && (
        <ul className="m-0 mb-2 list-disc pl-5 text-[13.5px] leading-relaxed text-slate-700">
          {keyPoints.map((p, i) => (
            <li key={i}>{p}</li>
          ))}
        </ul>
      )}
      {answer && <p className="m-0 whitespace-pre-wrap text-[13.5px] leading-relaxed text-slate-700">{answer}</p>}
    </div>
  );
}

function KeyRow({ n, prompt, children }: { n: number; prompt: string; children: React.ReactNode }) {
  return (
    <div className="mb-4 flex gap-3.5 border-b border-slate-100 pb-3.5 last:border-b-0" style={{ breakInside: 'avoid' }}>
      <div className="w-7 flex-none font-display text-sm font-bold tabular-nums text-teal-700">{n}</div>
      <div className="min-w-0 flex-1 text-sm">
        <p className="m-0 mb-1.5 text-[13px] text-slate-500">
          {prompt.length > 90 ? `${prompt.slice(0, 90)}…` : prompt}
        </p>
        {children}
      </div>
    </div>
  );
}

export default QuestionPack;
